import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, SearchX, Command, CornerDownLeft, ListTodo, Files, Rocket } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { search, type SearchResult, type SearchResultType } from '../packages/api-client/search-api';
import { hexAlpha } from '../utils/statusColors';
import { EntityIcon } from './taskIcons';
import { focusables } from '../packages/ui/focusable';

const TYPE_LABELS: Record<SearchResultType, { Icon: LucideIcon; label: string }> = {
  task: { Icon: ListTodo, label: 'Tasks' },
  record: { Icon: Files, label: 'Records' },
  project: { Icon: Rocket, label: 'Projects' },
};

const TYPE_ORDER: SearchResultType[] = ['task', 'record', 'project'];

function groupResults(results: SearchResult[]): Record<string, SearchResult[]> {
  const grouped: Record<string, SearchResult[]> = {};
  for (const r of results) {
    if (!grouped[r.type]) grouped[r.type] = [];
    grouped[r.type].push(r);
  }
  return grouped;
}

const OPEN_EVENT = 'ka:open-command-palette';

/** Opens the palette from a control (the topbar search); ⌘K / Ctrl+K still works. */
export function openCommandPalette() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  // ── Global ⌘K / Ctrl+K listener ──────────────────────────────────────────
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    const handleOpen = () => setOpen(true);
    window.addEventListener('keydown', handleKey);
    window.addEventListener(OPEN_EVENT, handleOpen);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener(OPEN_EVENT, handleOpen);
    };
  }, []);

  // ── Focus input when opened; return focus to the opener when closed ────────
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    setQuery('');
    setResults([]);
    setActiveIdx(0);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      clearTimeout(t);
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  // ── Debounced search ──────────────────────────────────────────────────────
  const doSearch = useCallback((q: string) => {
    if (!q.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await search(q.trim());
        setResults(resp.data);
        setActiveIdx(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 150);
  }, []);

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setQuery(v);
    doSearch(v);
  }

  // ── Keyboard navigation ───────────────────────────────────────────────────
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[activeIdx]) {
      navigate(results[activeIdx].route);
      setOpen(false);
    }
  }

  // aria-modal: keep Tab inside the palette (same wrap as packages/ui Dialog).
  function trapTab(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return;
    const els = focusables(e.currentTarget);
    if (els.length === 0) { e.preventDefault(); return; }
    const first = els[0];
    const last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function handleSelect(result: SearchResult) {
    navigate(result.route);
    setOpen(false);
  }

  if (!open) return null;

  const grouped = groupResults(results);
  // Flatten for keyboard index tracking
  let flatIdx = 0;

  return (
    <div className="command-palette-overlay" onClick={() => setOpen(false)}>
      <div className="command-palette" role="dialog" aria-modal="true" aria-label="Search workspace" onClick={e => e.stopPropagation()} onKeyDown={trapTab}>
        {/* Search input */}
        <div className="command-palette-input-wrap">
          <span className="command-palette-search-icon"><Search size={18} aria-hidden="true" /></span>
          <input
            ref={inputRef}
            className="command-palette-input"
            placeholder="Search tasks, records, projects…"
            aria-label="Search tasks, records, projects"
            value={query}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <div className="spinner spinner-sm" style={{ flexShrink: 0 }} />}
          <span className="command-palette-hint">ESC</span>
        </div>

        {/* Results */}
        <div className="command-palette-results">
          {query.trim() === '' && (
            <div className="command-palette-empty">
              <div style={{ marginBottom: '0.5rem' }}><Command size={32} aria-hidden="true" /></div>
              <div>Type to search across your workspace</div>
            </div>
          )}

          {query.trim() !== '' && !loading && results.length === 0 && (
            <div className="command-palette-empty">
              <div style={{ marginBottom: '0.5rem' }}><SearchX size={24} aria-hidden="true" /></div>
              <div>No results for <strong>"{query}"</strong></div>
            </div>
          )}

          {TYPE_ORDER.filter(t => grouped[t]?.length > 0).map(type => {
            const { Icon: TypeIcon, label } = TYPE_LABELS[type];
            return (
            <div key={type}>
              <div className="command-result-group-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TypeIcon size={14} aria-hidden="true" />{label}
              </div>
              {grouped[type].map(result => {
                const idx = flatIdx++;
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={result.id}
                    className={`command-result-item${isActive ? ' active' : ''}`}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setActiveIdx(idx)}
                  >
                    <span
                      className="command-result-icon"
                      style={{
                        background: result.color ? hexAlpha(result.color, 0.13) : 'var(--track)',
                        color: result.color || 'var(--color-text-muted)',
                      }}
                    >
                      <EntityIcon icon={result.icon} size={16} />
                    </span>
                    <span className="command-result-text">
                      <span className="command-result-title">{result.title}</span>
                      {result.subtitle && (
                        <span className="command-result-subtitle">{result.subtitle}</span>
                      )}
                    </span>
                    {isActive && <span className="command-result-enter"><CornerDownLeft size={14} aria-hidden="true" /></span>}
                  </button>
                );
              })}
            </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="command-palette-footer">
          <span>↑↓ navigate</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CornerDownLeft size={12} aria-hidden="true" /> open</span>
          <span>ESC close</span>
        </div>
      </div>
    </div>
  );
}
